import React, { useState, FormEvent, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import { useAuth, type AuthUser } from "../auth/AuthContext";
import { apiRequest, startGoogleSignIn } from "../api/client";
import { PUBLIC_APP_NAME } from "../config/publicBrand";
import { isFieldModeEnabled } from "../auth/FieldModeContext";
import "./auth.css";


function redirectAfterLogin(user: AuthUser, navigate: ReturnType<typeof useNavigate>): void {
    if (user.mustChangePassword) {
        if (user.role === 'admin') {
            navigate('/admin/settings?tab=security&force=true', { replace: true });
        } else {
            navigate('/account/password?force=true', { replace: true });
        }
        return;
    }
    if (user.role === 'admin') {
        navigate(isFieldModeEnabled() ? '/technician/dashboard' : '/admin/dashboard', { replace: true });
    } else {
        navigate('/technician/dashboard', { replace: true });
    }
}

function Login(): React.JSX.Element {

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { login, refreshSession } = useAuth();
    const [username, setUsername] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [oauthBusy, setOauthBusy] = useState(false);
    const [googleEnabled, setGoogleEnabled] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const cacheKey = 'zera.oauth-status.enabled';
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached === 'true' || cached === 'false') {
                setGoogleEnabled(cached === 'true');
            }
        } catch {
            // sessionStorage unavailable
        }

        (async () => {
            try {
                const res = await apiRequest<{ enabled?: boolean }>('/api/auth/oauth-status');
                if (!cancelled) {
                    const enabled = res.enabled ?? false;
                    setGoogleEnabled(enabled);
                    try {
                        sessionStorage.setItem(cacheKey, String(enabled));
                    } catch {
                        // ignore
                    }
                }
            } catch {
                if (!cancelled) setGoogleEnabled(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const oauthReturnHandled = useRef(false);

    useEffect(() => {
        const error = searchParams.get("error");
        if (error === "no_account") {
            alert("No active account for that Google email. Ask your admin to add you first.");
            setSearchParams({}, { replace: true });
            return;
        }
        if (error === "oauth_config") {
            alert(
                "Google sign-in is not configured on the server yet. Add GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET, AUTH_SECRET, and AUTH_URL=http://localhost:4000 to sutra/backend/.env.local, then restart the backend."
            );
            setSearchParams({}, { replace: true });
            return;
        }
        if (error === "oauth") {
            alert("Google sign-in failed. Please try again or use email and password.");
            setSearchParams({}, { replace: true });
            return;
        }

        if (searchParams.get("oauth") !== "success" || oauthReturnHandled.current) return;

        const exchange = searchParams.get("exchange");
        if (!exchange) {
            alert("Google sign-in did not finish correctly. Please try again.");
            setSearchParams({}, { replace: true });
            return;
        }

        oauthReturnHandled.current = true;

        void (async () => {
            setOauthBusy(true);
            try {
                const res = await apiRequest<{ user: { role?: string } | null }>("/api/auth/oauth-complete", {
                    method: "POST",
                    json: { exchange }
                });
                if (!res.user) {
                    oauthReturnHandled.current = false;
                    alert("Signed in with Google but session could not be loaded. Try again.");
                    return;
                }
                const sessionUser = await refreshSession();
                setSearchParams({}, { replace: true });
                if (sessionUser) {
                    redirectAfterLogin(sessionUser, navigate);
                } else {
                    navigate('/login', { replace: true });
                }
            } catch {
                oauthReturnHandled.current = false;
                alert("Signed in with Google but session could not be loaded. Try again.");
            } finally {
                setOauthBusy(false);
            }
        })();
    }, [searchParams, setSearchParams, refreshSession, navigate]);

    // 2) Form submit handler
    async function handleSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault(); // stops browser refresh
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const user = await login(username, password);
            redirectAfterLogin(user, navigate);
        } catch {
            alert("Invalid credentials. Please try again.");
        } finally {
            setIsSubmitting(false);
        }

    }

    return (
        <AppShell showNav={false} showFooter={false}>
            <div className="auth-page">
                <section className="auth-hero">
                    <p className="auth-kicker">{PUBLIC_APP_NAME}</p>
                    <h1 className="auth-title">Welcome back.</h1>
                    <p className="auth-subtitle">
                        Log in to manage inspections, work orders, and field operations across every site.
                    </p>
                </section>

                <section className="auth-panel">
                    <div className="auth-brand">
                        <img className="auth-brand-logo" src="/logo.svg" alt="Logo" />
                        <span>{PUBLIC_APP_NAME}</span>
                    </div>
                    <h2>Sign in</h2>
                    <form className="auth-form" onSubmit={handleSubmit}>
                        <div>
                            <label htmlFor="username">Email</label>
                            <input
                                type="email"
                                id="username"
                                name="username"
                                required
                                disabled={isSubmitting}
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="you@company.com"
                            />
                        </div>

                        <div>
                            <label htmlFor="password">Password</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                required
                                disabled={isSubmitting}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                            />
                        </div>

                        <div className="auth-actions">
                            <button
                                className="auth-btn auth-btn-primary"
                                type="submit"
                                disabled={isSubmitting || oauthBusy}
                            >
                                {isSubmitting ? "Signing in..." : "Sign in"}
                            </button>

                            {googleEnabled ? (
                                <>
                                    <div className="auth-divider" aria-hidden>
                                        <span>or</span>
                                    </div>

                                    <button
                                        type="button"
                                        className="auth-btn-google"
                                        disabled={isSubmitting || oauthBusy}
                                        onClick={() => startGoogleSignIn()}
                                        aria-busy={oauthBusy}
                                    >
                                        <span className="auth-btn-google__icon" aria-hidden>
                                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                                    fill="#4285F4"
                                                />
                                                <path
                                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                                    fill="#34A853"
                                                />
                                                <path
                                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                                    fill="#FBBC05"
                                                />
                                                <path
                                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                                    fill="#EA4335"
                                                />
                                            </svg>
                                        </span>
                                        <span className="auth-btn-google__label">
                                            {oauthBusy ? "Completing Google sign-in…" : "Sign in with Google"}
                                        </span>
                                        {oauthBusy ? (
                                            <span className="auth-btn-google__spinner" aria-hidden />
                                        ) : null}
                                    </button>
                                </>
                            ) : null}

                            <button
                                className="auth-btn auth-btn-secondary"
                                type="button"
                                disabled={isSubmitting || oauthBusy}
                                onClick={() => navigate("/register?mode=company")}
                            >
                                Create company account
                            </button>
                        </div>
                    </form>
                    <p className="auth-help">
                        Need employee access? Ask your admin to add you, then sign in here.
                    </p>
                </section>
            </div>
        </AppShell>
    );
}

export default Login;
